import { alpha, Box, useMediaQuery } from "@mui/material";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GridLayout } from "react-grid-layout";
import {
    gridBounds,
    minMaxSize,
    verticalCompactor,
} from "react-grid-layout/core";
import type {
    DragConfig,
    EventCallback,
    GridConfig,
    LayoutConstraint,
    LayoutItem as GridItem,
    ResizeConfig,
    ResizeHandleAxis,
} from "react-grid-layout/core";
import { useResizeObserver } from "utils/useResizeObserver";
import { SignalsBaseObjectWrapper } from "../../BaseObjects/SignalsBaseObjectWrapper";
import type DashboardStore from "../DashboardStore";
import { CardLayoutContext } from "./CardLayoutContext";
import type { CardLayout } from "./CardLayoutContext";
import { fromGridItem } from "./geometry";
import { HiddenCardPlaceholder } from "./HiddenCardPlaceholder";
import {
    CARD_RADIUS,
    DRAG_HANDLE_CLASS,
    GRID_ARRANGE_MEDIA_QUERY,
    RESIZE_OUTLINE_CLASS,
} from "./grid";
import {
    GRID_COLUMNS,
    GRID_GUTTER_PX,
    GRID_ROW_HEIGHT_PX,
} from "./layoutConfig";
import {
    createGridBackgroundImage,
    createResizeOutlineController,
    createSnappedPositionStrategy,
    getPointerPosition,
} from "./resizePreview";
import { sortLayoutObjects } from "./seed";
import type { LayoutItem } from "./types";

const GRID_ITEM_SELECTOR = ".react-grid-item";

/**
 * `GridItem` is memoised with a comparator that checks `margin` and
 * `containerPadding` by reference, so inline array literals would re-render
 * every card on every grid render.
 */
const GRID_MARGIN: readonly [number, number] = [0, 0];
const GRID_CONTAINER_PADDING: readonly [number, number] = [0, 0];
const GRID_RESIZE_HANDLES: readonly ResizeHandleAxis[] = ["se"];

const byVisualOrder = (left: LayoutItem, right: LayoutItem) =>
    left.y - right.y ||
    left.x - right.x ||
    left.objectId.localeCompare(right.objectId);

/**
 * Renders a dashboard's cards as an arrangeable grid. It is a projection of
 * the layout store: it renders the store's rendered layout and reports
 * gestures back. Below the breakpoint it becomes the narrow view: no grid,
 * just every card full width in one column, in `order`.
 *
 * A card the viewer cannot see renders as a placeholder that holds its place,
 * which can be moved but not resized. The narrow view leaves it out.
 *
 * This uses react-grid-layout's current `GridLayout`, not the `legacy`
 * wrapper the old dashboards use, because the wrapper does not forward
 * `positionStrategy` and pins the drag threshold to zero.
 */
export const SignalsDashboardGrid = observer(
    ({
        dashboardStore,
        attachRef,
    }: {
        dashboardStore: DashboardStore;
        attachRef?: (
            componentIds: string[],
        ) => (el: HTMLElement | null) => void;
    }) => {
        const layoutStore = dashboardStore.dashboardLayoutStore;
        const { theRef: containerRef, elementWidth: measuredWidth = 0 } =
            useResizeObserver<HTMLDivElement>({ box: "border" });

        // Presentation mode narrows the container; freezing heights keeps
        // cards from rewrapping and moving while a dashboard is presented.
        const isPresentationMode = dashboardStore.isPresentationMode;
        useEffect(() => {
            if (isPresentationMode) layoutStore.freezeHeights();
            else layoutStore.unfreezeHeights();
        }, [isPresentationMode, layoutStore]);

        useEffect(() => () => layoutStore.cancelEditing(), [layoutStore]);

        // A new card lands at the bottom, so bring it into view once it renders.
        // An instant jump, not a smooth scroll: the viewport lazy loader fetches
        // every card that passes through the viewport.
        useEffect(
            () =>
                layoutStore.subscribeCardsAdded((objectIds) => {
                    const targetId = objectIds[objectIds.length - 1];
                    let attempts = 0;
                    const scrollToCard = () => {
                        const element = document.getElementById(targetId);
                        if (element) {
                            element.scrollIntoView({
                                behavior: "instant",
                                block: "start",
                            });
                        } else if (++attempts < 20) {
                            window.setTimeout(scrollToCard, 100);
                        }
                    };
                    window.setTimeout(scrollToCard, 100);
                }),
            [layoutStore],
        );

        const isWideEnoughToArrange = useMediaQuery(GRID_ARRANGE_MEDIA_QUERY);
        const isEditing = layoutStore.isEditing;
        const canArrange = isEditing && isWideEnoughToArrange;

        const cardLayout = useMemo<CardLayout>(
            () => ({ layoutStore, isEditing, canArrange }),
            [layoutStore, isEditing, canArrange],
        );

        const componentById = new Map(
            dashboardStore.components.map((component) => [
                component.id,
                component,
            ]),
        );
        const sizeConstraints = layoutStore.sizeConstraints;
        const isHidden = dashboardStore.isHiddenCard;
        const renderedItems = layoutStore.renderedLayout.items.filter(
            (item) =>
                componentById.has(item.objectId) || isHidden(item.objectId),
        );
        const items = renderedItems.sort(byVisualOrder);
        const heightById = new Map(
            renderedItems.map((item) => [item.objectId, item.height]),
        );

        /** A card, or the placeholder for one the viewer cannot see. */
        const renderCard = (objectId: string) => {
            const component = componentById.get(objectId);
            if (!component) {
                return isHidden(objectId) ? (
                    <HiddenCardPlaceholder objectId={objectId} />
                ) : null;
            }
            return (
                <SignalsBaseObjectWrapper
                    componentStore={component}
                    observerRef={attachRef?.([component.id])}
                />
            );
        };

        const gridLayout: GridItem[] = items.map((item) => {
            const constraints = sizeConstraints.get(item.objectId);
            return {
                i: item.objectId,
                x: item.x,
                y: item.y,
                w: item.width,
                h: item.height,
                minW: constraints?.minWidth ?? 1,
                minH: constraints?.minHeight ?? 1,
                maxW: constraints?.maxWidth ?? GRID_COLUMNS,
                maxH: constraints?.maxHeight,
                isDraggable: canArrange,
                isResizable: canArrange && !isHidden(item.objectId),
            };
        });

        /**
         * Read by the pointer handlers instead of closing over render values.
         * `GridItem` compares handlers by reference, so handlers rebuilt each
         * render would re-render every card.
         */
        const latest = useRef({ canArrange, measuredWidth });
        latest.current = { canArrange, measuredWidth };

        const resizeOutline = useMemo(createResizeOutlineController, []);
        useEffect(() => () => resizeOutline.stop(), [resizeOutline]);

        /** Rebuilt only when a cell changes size. */
        const positionStrategy = useMemo(
            () =>
                createSnappedPositionStrategy(
                    measuredWidth / GRID_COLUMNS,
                    GRID_ROW_HEIGHT_PX,
                ),
            [measuredWidth],
        );

        const gridConfig = useMemo<Partial<GridConfig>>(
            () => ({
                cols: GRID_COLUMNS,
                rowHeight: GRID_ROW_HEIGHT_PX,
                margin: GRID_MARGIN,
                containerPadding: GRID_CONTAINER_PADDING,
            }),
            [],
        );

        /**
         * `bounded` must stay off. It adds the renderer's `containerBounds`
         * constraint, which clamps resizing to the resize handle's
         * `offsetParent` height. That parent is the card itself, so a card
         * could never be resized taller, and a card low in the grid would
         * collapse to one row. `gridBounds` already keeps x and y in range.
         */
        const dragConfig = useMemo<Partial<DragConfig>>(
            () => ({
                enabled: canArrange,
                handle: `.${DRAG_HANDLE_CLASS}`,
            }),
            [canArrange],
        );

        const resizeConfig = useMemo<Partial<ResizeConfig>>(
            () => ({ enabled: canArrange, handles: GRID_RESIZE_HANDLES }),
            [canArrange],
        );

        /**
         * Applies the live content floor during a resize. The renderer reads
         * per-item `minH` only when a gesture starts, but it consults size
         * constraints every frame, so a floor that rises as a card is narrowed
         * is enforced here. The constraint reads the store when it runs, so the
         * array never has to be rebuilt mid-gesture.
         */
        const constraints = useMemo<LayoutConstraint[]>(
            () => [
                gridBounds,
                minMaxSize,
                {
                    name: "signalsLiveContentFloor",
                    constrainSize: (item, w, h) => {
                        const floor = layoutStore.liveSizeConstraints(
                            item.i,
                        )?.minHeight;
                        return {
                            w,
                            h: floor === undefined ? h : Math.max(h, floor),
                        };
                    },
                },
            ],
            [layoutStore],
        );

        /**
         * Re-renders while a card is dragged so it follows the pointer. The
         * renderer keeps the dragged position in a ref, and its memoised
         * `GridItem` only re-renders when x, y, w, h or children change, which
         * happens once per cell. A fresh render hands it new children and it
         * repaints from that ref. Resizes don't need this: they paint at whole
         * cells and the outline follows the pointer.
         */
        const [, setDragFrame] = useState(0);
        const handleDrag = useCallback<EventCallback>(() => {
            setDragFrame((frame) => frame + 1);
        }, []);

        const handleDragStop = useCallback<EventCallback>(
            (nextLayout) => {
                if (latest.current.canArrange) {
                    layoutStore.updateDraftItems(nextLayout.map(fromGridItem));
                }
                layoutStore.endGesture();
            },
            [layoutStore],
        );

        const handleResizeStart = useCallback<EventCallback>(
            (_nextLayout, _oldItem, newItem, _placeholder, event, element) => {
                layoutStore.beginGesture();

                const { canArrange: arranging, measuredWidth: width } =
                    latest.current;
                if (!arranging || !newItem || width <= 0) return;

                const card = element?.closest<HTMLElement>(GRID_ITEM_SELECTOR);
                const pointer = getPointerPosition(event);
                if (!card || !pointer) return;

                const columnWidthPx = width / GRID_COLUMNS;
                const objectId = newItem.i;
                // Asked every frame, because the floor rises as the card is
                // narrowed and its content wraps.
                resizeOutline.start(card, pointer, () => {
                    const limits = layoutStore.liveSizeConstraints(objectId);
                    return {
                        minWidthPx: (limits?.minWidth ?? 1) * columnWidthPx,
                        maxWidthPx: (GRID_COLUMNS - newItem.x) * columnWidthPx,
                        minHeightPx:
                            (limits?.minHeight ?? 1) * GRID_ROW_HEIGHT_PX,
                        maxHeightPx:
                            limits?.maxHeight === undefined
                                ? Number.POSITIVE_INFINITY
                                : limits.maxHeight * GRID_ROW_HEIGHT_PX,
                    };
                });
            },
            [layoutStore, resizeOutline],
        );

        const handleResize = useCallback<EventCallback>(
            (_nextLayout, _oldItem, _newItem, _placeholder, event) => {
                resizeOutline.track(getPointerPosition(event));
            },
            [resizeOutline],
        );

        /** A height resize is the one gesture that hands a card's height to the user. */
        const handleResizeStop = useCallback<EventCallback>(
            (nextLayout, oldItem, newItem) => {
                resizeOutline.stop();

                if (latest.current.canArrange) {
                    if (newItem && newItem.h !== oldItem?.h) {
                        layoutStore.markUserSized(newItem.i);
                    }
                    layoutStore.updateDraftItems(nextLayout.map(fromGridItem));
                }
                layoutStore.endGesture();
            },
            [layoutStore, resizeOutline],
        );

        return (
            <Box
                ref={containerRef}
                className="signals-dashboard-layout-grid"
                sx={(theme) => ({
                    width: "100%",
                    minWidth: 0,
                    // The empty cells, drawn behind the opaque cards while
                    // arranging so the snap points are visible.
                    ...(canArrange && {
                        backgroundImage: createGridBackgroundImage({
                            containerWidthPx: measuredWidth,
                            columns: GRID_COLUMNS,
                            rowHeightPx: GRID_ROW_HEIGHT_PX,
                            insetPx: GRID_GUTTER_PX,
                            radiusPx:
                                Number(theme.shape.borderRadius) * CARD_RADIUS,
                            color: alpha(theme.palette.primary.main, 0.07),
                        }),
                        backgroundRepeat: "repeat-y",
                        backgroundPosition: "0 0",
                    }),
                    "& .react-grid-item": {
                        minWidth: 0,
                        padding: `${GRID_GUTTER_PX}px`,
                        boxSizing: "border-box",
                    },
                    "& .react-grid-placeholder": {
                        borderRadius: CARD_RADIUS,
                        backgroundColor: theme.palette.primary.main,
                        opacity: 0.14,
                    },
                    // Above the card's contents and below the resize handle.
                    [`& .${RESIZE_OUTLINE_CLASS}`]: {
                        zIndex: 5,
                        borderRadius: CARD_RADIUS,
                        border: `2px solid ${theme.palette.primary.main}`,
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.06,
                        ),
                    },
                    // The resize handle is a thin L in the card's bottom-right
                    // corner, inside a larger hit area. The `-se` class
                    // outranks the library's `bottom: 0; right: 0`, which
                    // would put the handle in the gutter outside the card.
                    "& .react-grid-item > .react-resizable-handle.react-resizable-handle-se":
                        {
                            zIndex: 6,
                            width: 24,
                            height: 24,
                            right: GRID_GUTTER_PX,
                            bottom: GRID_GUTTER_PX,
                            padding: 0,
                            background: "none",
                            color: theme.palette.text.secondary,
                            "&:hover": { color: theme.palette.primary.main },
                        },
                    "& .react-grid-item > .react-resizable-handle::after": {
                        right: 5,
                        bottom: 5,
                        width: 8,
                        height: 8,
                        borderRight: "1.5px solid currentColor",
                        borderBottom: "1.5px solid currentColor",
                    },
                    [`& .react-grid-item.resizing > .react-resizable-handle, & .react-grid-item.react-draggable-dragging .${DRAG_HANDLE_CLASS}`]:
                        { color: theme.palette.primary.main },
                    // Where there is a pointer to hover with, the move and
                    // resize controls show only on the card under it, on the
                    // card being moved or resized, and on a card holding
                    // keyboard focus. Touch screens always show them.
                    "@media (hover: hover)": {
                        [`& .react-grid-item .${DRAG_HANDLE_CLASS}, & .react-grid-item > .react-resizable-handle`]:
                            { opacity: 0, transition: "opacity 120ms" },
                        [`& .react-grid-item:is(:hover, :focus-within, .resizing, .react-draggable-dragging) .${DRAG_HANDLE_CLASS}, & .react-grid-item:is(:hover, :focus-within, .resizing, .react-draggable-dragging) > .react-resizable-handle`]:
                            { opacity: 1 },
                    },
                })}
            >
                {/* Always mounted, so announcements made while leaving
                    arrange mode are still read out. Sizes are strings because
                    MUI reads a bare 1 as 100%, which made the page scroll
                    sideways. Empty during export so the PDF can't print it. */}
                <Box
                    aria-live="polite"
                    role="status"
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "1px",
                        height: "1px",
                        margin: "-1px",
                        padding: 0,
                        border: 0,
                        overflow: "hidden",
                        clip: "rect(0 0 0 0)",
                        whiteSpace: "nowrap",
                    }}
                >
                    {dashboardStore.isExportCaptureInProgress
                        ? null
                        : layoutStore.announcement}
                </Box>
                <CardLayoutContext.Provider value={cardLayout}>
                    {!isWideEnoughToArrange &&
                        sortLayoutObjects(dashboardStore.layoutObjects).map(
                            (object) => {
                                const height = heightById.get(object.id);
                                if (object.placeholderSize || !height)
                                    return null;

                                return (
                                    <Box
                                        key={object.id}
                                        sx={{
                                            padding: `${GRID_GUTTER_PX}px`,
                                            height: height * GRID_ROW_HEIGHT_PX,
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        {renderCard(object.id)}
                                    </Box>
                                );
                            },
                        )}
                    {isWideEnoughToArrange && measuredWidth > 0 && (
                        <GridLayout
                            autoSize
                            compactor={verticalCompactor}
                            constraints={constraints}
                            dragConfig={dragConfig}
                            gridConfig={gridConfig}
                            layout={gridLayout}
                            onDrag={canArrange ? handleDrag : undefined}
                            onDragStart={layoutStore.beginGesture}
                            onDragStop={handleDragStop}
                            onResize={handleResize}
                            onResizeStart={handleResizeStart}
                            onResizeStop={handleResizeStop}
                            positionStrategy={positionStrategy}
                            resizeConfig={resizeConfig}
                            width={measuredWidth}
                        >
                            {items.map((item) => (
                                <div key={item.objectId}>
                                    {renderCard(item.objectId)}
                                </div>
                            ))}
                        </GridLayout>
                    )}
                </CardLayoutContext.Provider>
            </Box>
        );
    },
);

export default SignalsDashboardGrid;
